import { FormLeavingListener } from '@exam-management/components';
import {
  useCreateInDevelopmentQuestionMutation,
  GetQuestionCreationTaskByIdDocument,
} from '@exam-management/graphql';
import { type QBankStructureLevel } from '@exam-management/qbs-provider';
import { getLocationDefaultValue } from '@exam-management/qcontent-management';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useNavigate } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { matchPath, useLocation } from 'react-router-dom';

import { DEFAULT_FORM_VALUES } from './constants';
import {
  type SingleQuestionCreationFormInput,
  getSingleQuestionSchema,
} from './single-question-creation.schema';
import { parseQuestionFormDataForApi } from './utils';

interface QuestionCreationFormProps extends PropsWithChildren {
  qbsLevels: QBankStructureLevel[];
}

export default function SingleQuestionCreationForm(props: Readonly<QuestionCreationFormProps>) {
  const { children, qbsLevels } = props;

  const { task } = useQuestionCreationTaskDetails();
  const navigate = useNavigate();

  const SingleQuestionCreationSchema = useMemo(
    () => getSingleQuestionSchema(qbsLevels),
    [qbsLevels],
  );

  const defaultLocationValues = useMemo(() => getLocationDefaultValue(qbsLevels), [qbsLevels]);

  const location = useLocation();
  const matchedTaskManagement = matchPath('/task-management/*', location.pathname);

  const formContext = useForm<SingleQuestionCreationFormInput>({
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      location: {
        ...defaultLocationValues,
      },
    },
    resolver: zodResolver(SingleQuestionCreationSchema),
  });
  const { showSnackbar } = useSnackbar();

  const [createInDevelopmentQuestion] = useCreateInDevelopmentQuestionMutation({
    onError: () => {
      showSnackbar({
        message: 'Unable to Save Question',
        variant: 'error',
      });
    },
  });

  const handleShowRequiredFieldsError = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };

  const handleSingleQuestionCreationFormSubmit = async (
    formData: SingleQuestionCreationFormInput,
  ) => {
    const qBankStructureLevelItemId = formData.location.SUB_TOPIC?.value as string;
    const qBankStructureId = task.metadata?.requirement.qBankStructure?.id;

    if (!qBankStructureLevelItemId || !qBankStructureId)
      throw Error('Invalid qbs id and level item id');

    const payload = parseQuestionFormDataForApi(formData, parseContentForApi);

    const result = await createInDevelopmentQuestion({
      variables: {
        input: {
          qBankStructureLevelItemId,
          refQBankStructureId: qBankStructureId,
          data: payload,
        },
        taskId: task.id,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: task.id,
            },
          },
        },
      ],
      onCompleted: (data) => {
        showSnackbar({
          message: 'Question has been saved',
          variant: 'success',
        });

        navigate(
          matchedTaskManagement
            ? '/task-management/question-creation/:taskId/single/:questionId'
            : '/my-task/question-creation/:taskId/single/:questionId',
          {
            params: {
              taskId: task.id,
              questionId: data?.qctCreateInDevelopmentQuestion.id,
            },
          },
        );
      },
    });

    if (result.errors) throw result.errors;
  };

  return (
    <FormProvider {...formContext}>
      <Form
        onSubmit={formContext.handleSubmit(
          handleSingleQuestionCreationFormSubmit,
          handleShowRequiredFieldsError,
        )}
      >
        {children}
      </Form>
      <FormLeavingListener onSubmit={handleSingleQuestionCreationFormSubmit} />
    </FormProvider>
  );
}
